import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteChrome from './SiteChrome';

const posts = {
  'how-to-sell-an-app-to-your-audience-without-sounding-pushy': {
    category: 'Creator Sales',
    date: 'April 5, 2026',
    readTime: '5 min read',
    title: 'How to Sell an App to Your Audience Without Sounding Pushy',
    intro:
      'Selling an app well is usually about relevance and trust, not pressure. The strongest creator promotions make the app feel useful inside a real problem people already want to solve.',
    sections: [
      {
        title: 'Start with a real pain point',
        paragraphs: [
          'People do not usually respond to features first. They respond to friction they already feel in daily life.',
          'Lead with the problem: inconsistent habits, messy planning, weak reflection, or spending patterns they want to understand better. Then position the app as a tool that helps with that.',
        ],
      },
      {
        title: 'Show where the app fits in a routine',
        paragraphs: [
          'Apps convert better when followers can imagine using them in a moment they already understand.',
          'Examples include a morning planning routine, a night reflection habit, a weekly reset, or a spending check-in. That makes the recommendation feel practical instead of abstract.',
        ],
      },
      {
        title: 'Use a calm call to action',
        paragraphs: [
          'A clear, believable CTA works better than hype. Try language like: “If you want a better way to reflect and track your patterns, try this app.”',
          'People trust recommendations more when they feel specific, useful, and honest.',
        ],
      },
    ],
    nextSlug: 'how-to-promote-products-to-followers-without-losing-trust',
  },
  'how-to-promote-products-to-followers-without-losing-trust': {
    category: 'Audience Trust',
    date: 'April 5, 2026',
    readTime: '6 min read',
    title: 'How to Promote Products to Followers Without Losing Trust',
    intro:
      'Followers rarely dislike promotion itself. What they reject is promotion that feels disconnected, exaggerated, or too obviously transactional.',
    sections: [
      {
        title: 'Promote products that match your audience',
        paragraphs: [
          'The easiest way to lose trust is to push offers that feel random compared to the content people know you for.',
          'When the product matches your audience’s goals, the promotion feels like a recommendation instead of an interruption.',
        ],
      },
      {
        title: 'Teach first, recommend second',
        paragraphs: [
          'One of the best promotion formats is simple: give a useful idea first, then show the product that helps apply it.',
          'That might be a post about building consistency, restarting a routine, or reflecting more honestly, followed by the app or product you use to support that process.',
        ],
      },
      {
        title: 'Protect trust with compliant language',
        paragraphs: [
          'Do not promise outcomes you cannot support, and avoid clinical, crisis, or regulated-advice claims.',
          'Stronger trust usually comes from grounded language like “this can help you reflect more consistently” instead of dramatic claims like “this will fix everything”.',
        ],
      },
    ],
    nextSlug: 'content-angles-that-help-creators-convert-better',
  },
  'content-angles-that-help-creators-convert-better': {
    category: 'Content Strategy',
    date: 'April 5, 2026',
    readTime: '4 min read',
    title: 'Content Angles That Help Creators Convert Better',
    intro:
      'Better conversion usually comes from stronger angles, not just more posting. A good angle helps followers immediately see why the product matters to them.',
    sections: [
      {
        title: 'Use transformation angles people already want',
        paragraphs: [
          'Good product content often starts with a shift people care about: more discipline, clearer planning, better habits, or stronger self-awareness.',
          'These angles work because they connect the product to progress people are already trying to make.',
        ],
      },
      {
        title: 'Demonstrate instead of explaining',
        paragraphs: [
          'Showing a product inside your real routine often converts better than listing features.',
          'A quick demonstration, habit flow, or before-and-after process change helps followers picture themselves using it.',
        ],
      },
      {
        title: 'Use identity angles carefully',
        paragraphs: [
          'Messages like “for people trying to become more consistent” or “for people who want to understand their patterns better” can work well because they feel personal.',
          'Keep those angles grounded and believable. Avoid overpromising and stay focused on useful, practical benefits.',
        ],
      },
    ],
    nextSlug: 'how-to-sell-an-app-to-your-audience-without-sounding-pushy',
  },
};

const AffiliateBlogPost = () => {
  const { slug } = useParams();
  const post = posts[slug] || posts['how-to-sell-an-app-to-your-audience-without-sounding-pushy'];
  const nextPost = posts[post.nextSlug];

  return (
    <SiteChrome>
      <main className="py-12 px-4 sm:px-6">
        <article className="max-w-4xl mx-auto">
          <Link to="/#affiliate-posts" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 mb-8">
            &lt;- Back to affiliate posts
          </Link>

          <header className="bg-white border border-gray-100 rounded-[2rem] p-8 sm:p-12 shadow-sm mb-10">
            <p className="text-xs uppercase tracking-[0.35em] text-primary-700 font-semibold mb-4">{post.category}</p>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6">{post.title}</h1>
            <p className="text-base text-gray-600 leading-7 mb-8">{post.intro}</p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <span>{post.date}</span>
              <span className="hidden sm:inline">/</span>
              <span>{post.readTime}</span>
            </div>
          </header>

          <div className="bg-white border border-gray-100 rounded-[2rem] p-8 sm:p-12 shadow-sm mb-10">
            {post.sections.map((section) => (
              <section key={section.title} className="mb-12 last:mb-0">
                <h2 className="text-xl font-bold text-gray-900 mb-5">{section.title}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph} className="text-sm sm:text-base text-gray-700 leading-7 mb-5 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-6 text-sm text-amber-900 leading-7 mb-10">
            Keep affiliate messaging practical and compliant. Focus on reflection, routines, planning, habits, or product use cases. Avoid clinical claims, crisis-support claims, or regulated legal and financial advice language.
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <Link
              to="/"
              className="inline-flex justify-center items-center px-6 py-4 rounded-2xl border border-gray-200 bg-white text-sm text-gray-700 font-semibold hover:border-primary-200 hover:text-primary-700 transition-colors"
            >
              Affiliate home
            </Link>
            <Link
              to={`/blog/${post.nextSlug}`}
              className="inline-flex justify-center items-center px-6 py-4 rounded-2xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            >
              {nextPost.title}
            </Link>
          </div>
        </article>
      </main>
    </SiteChrome>
  );
};

export default AffiliateBlogPost;
